/// <amd-module name="DistributedTaskUI/ServiceEndpoints/Editor/Components/EndpointsEditorPanel" />
import * as React from "react";
import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { Panel } from "VSSUI/Panel";
import { TitleSize } from "VSSUI/Header";
import { EndpointsEditorPanelContentType, ActionsCreatorInstanceIds } from "DistributedTaskUI/ServiceEndpoints/Editor/Constants";
import { EndpointsEditorActionsCreator } from "DistributedTaskUI/ServiceEndpoints/Editor/Actions/EndpointsEditorActionsCreator";
import { EndpointsEditorStore } from "DistributedTaskUI/ServiceEndpoints/Editor/Stores/EndpointsEditorStore";
import { EndpointTypesSelectionComponent } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/EndpointTypesSelectionComponent";
import { EndpointAuthSchemeSelectionComponent } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/EndpointAuthSchemeSelectionComponent";
import { EndpointEditorComponentContainer } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/EndpointEditorComponentContainer";
import { IEndpointsEditorPanelProps, IEndpointsEditorPanelState, IEndpointAuthSchemeSelectionPanelState } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/EndpointsEditorPanelInterfaces";
import { StoreManager } from "DistributedTask/Flux/StoreManager";
import { ActionCreatorManager } from "DistributedTask/Flux/ActionCreatorManager";
import { ServiceEndpoint, ServiceEndpointType } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";

import { IVssPageContext } from "VSS/Platform/Context";

export class EndpointsEditorPanel extends VssComponent<IEndpointsEditorPanelProps, IEndpointsEditorPanelState> {

    constructor(props: IEndpointsEditorPanelProps, context: IVssComponentContext) {
        super(props, context);

        this._actionCreator = ActionCreatorManager.
            CreateActionCreator<EndpointsEditorActionsCreator, IVssPageContext>
            (EndpointsEditorActionsCreator, ActionsCreatorInstanceIds.EndpointsEditorActionsCreatorInstanceId, this.context.pageContext);
        StoreManager.GetStore<EndpointsEditorStore>(EndpointsEditorStore, ActionsCreatorInstanceIds.EndpointsEditorStoreInstanceId, this.context.pageContext);
        let contentType = this.props.contentType;
        if (!contentType) {
            contentType = EndpointsEditorPanelContentType.UpdateForm;
        }

        if (props.endpointType) {
            this._actionCreator.initializeServiceEndpointType(props.endpointType);
        }

        this.state = {
            contentType: contentType,
            previousContentType: contentType,
            panelTitle: this.props.panelTitle,
            endpointTypesPanelState: null,
            panelDescription: this.props.panelDescription
        };
    }

    public componentWillUnmount() {
        ActionCreatorManager.DeleteActionCreator(EndpointsEditorActionsCreator, ActionsCreatorInstanceIds.EndpointsEditorActionsCreatorInstanceId);
        StoreManager.DeleteStore(EndpointsEditorStore, ActionsCreatorInstanceIds.EndpointsEditorStoreInstanceId);
    }

    public render(): React.ReactNode {
        return (
            <Panel
                modal={true}
                lightDismiss={false}
                className="endpoints-editor-panel-container"
                description={this.state.panelDescription}
                onDismiss={this.props.onDismiss }
                titleProps={{ text: this.state.panelTitle, size: TitleSize.Large, className: "endpoints-editor-panel-heading" }}
            >
                {this._renderComponent()}
            </Panel>
        );
    }

    private _updateComponent = (panelState: IEndpointsEditorPanelState) => {
        this.setState({
            contentType: panelState.contentType,
            previousContentType: panelState.previousContentType,
            panelTitle: panelState.panelTitle,
            endpointTypesPanelState: panelState.endpointTypesPanelState || this.state.endpointTypesPanelState,
            panelDescription: panelState.panelDescription,
            authSchemeSelectionPanelState: panelState.authSchemeSelectionPanelState || this.state.authSchemeSelectionPanelState,
        });
    }

    private _renderComponent() {
        switch (this.state.contentType) {
            case EndpointsEditorPanelContentType.ServiceEndpointTypes:
                return this._getServiceEndpointTypesSelectionComponent();
            case EndpointsEditorPanelContentType.CreateForm:
                return this._getEndpointCreatePanel();
            case EndpointsEditorPanelContentType.UpdateForm:
                return this._getEndpointEditPanel({...this.props.endpoint});
            case EndpointsEditorPanelContentType.EndpointAuthSchemes:
                return this._getAuthSchemeSelectionPanel();
            default:
                return this._getServiceEndpointTypesSelectionComponent();
        }
    }

    private _getServiceEndpointTypesSelectionComponent(): JSX.Element {
        return (
            <EndpointTypesSelectionComponent
                groupedEndpointTypes={this.state.endpointTypesPanelState ? this.state.endpointTypesPanelState.groupedEndpointTypes : [] }
                selectedEndpointType={this.state.endpointTypesPanelState ? this.state.endpointTypesPanelState.selectedEndpointType : null }
                updateComponent={this._updateComponent}
                onDismiss={this.props.onDismiss}
                onPrimaryAction={this.props.onPrimaryAction}
            />
        );
    }

    private _getEndpointCreatePanel(): JSX.Element {
        return (
            <EndpointEditorComponentContainer
                updateComponent={this._updateComponent}
                primaryOnClick={this.props.onPrimaryAction}
                onRefresh={this.props.onRefresh}
                onDismiss={this.props.onDismiss}
                contentType={EndpointsEditorPanelContentType.CreateForm}
                sourceContentType={this.state.previousContentType}
                authSchemeSelectionPanelState={this.state.authSchemeSelectionPanelState}
                hideBackButton={this.props.hideBackButton}
                disableAllPipelinesSetting={this.props.disableAllPipelinesSetting}
            />
        );
    }

    private _getEndpointEditPanel(endpoint: ServiceEndpoint): JSX.Element {
        this._actionCreator.initializeEditState(endpoint);
        return (
            <EndpointEditorComponentContainer
                disableAllPipelinesSetting={this.props.disableAllPipelinesSetting}
                updateComponent={this._updateComponent}
                primaryOnClick={this.props.onPrimaryAction}
                onRefresh={this.props.onRefresh}
                onDismiss={this.props.onDismiss}
                contentType={EndpointsEditorPanelContentType.UpdateForm}
                sourceContentType={this.state.previousContentType}
                hideBackButton={this.props.hideBackButton}
            />
        );
    }

    private _getAuthSchemeSelectionPanel(): JSX.Element {
        const authSchemeState: IEndpointAuthSchemeSelectionPanelState = this.state.authSchemeSelectionPanelState || {};
        let endpointType: ServiceEndpointType;
        if (this.props.endpointType) { // task editor flow
            endpointType = this.props.endpointType;
        }
        else if (this.state.endpointTypesPanelState && this.state.endpointTypesPanelState.selectedEndpointType &&
                    this.state.endpointTypesPanelState.selectedEndpointType.endpointTypes && this.state.endpointTypesPanelState.selectedEndpointType.endpointTypes.length > 0) {
                        endpointType = this.state.endpointTypesPanelState.selectedEndpointType.endpointTypes[0];
        }

        return (
            <EndpointAuthSchemeSelectionComponent
                updateComponent={panelState => this._updateComponent(panelState)}
                endpointType={endpointType}
                selectedAuthSchemeIndex={authSchemeState.selectedAuthSchemeIndex}
                hideBackButton={this.props.hideBackButton}
            />
        );
    }

    private _actionCreator: EndpointsEditorActionsCreator;
}

VssComponent.register("endpoints-editor-panel", EndpointsEditorPanel);